import React from "react";
import PropTypes from "prop-types";
import { FieldLabel } from "./PageShell";

/**
 * Input/select "Geist" thật sự có khung — cùng công thức với SiteSelect/UserPicker
 * (tw-h-10 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm...).
 * Thay cho FormInput/FormSelect chung của app (tw-border-slate-300, không appearance-none,
 * không set height cố định) — trên nền trắng viền slate-300 quá nhạt + input/select native
 * chrome không bị reset nên nhìn như chỉ có gạch chân, không giống ô nhập chuyên nghiệp.
 */
const BASE =
  "tw-w-full tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg " +
  "tw-px-3 tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3 tw-appearance-none " +
  "hover:tw-border-geist-border-2 focus:tw-border-geist-accent focus:tw-outline-none " +
  "focus:tw-ring-2 focus:tw-ring-geist-accent/20 tw-transition-colors " +
  "disabled:tw-bg-geist-bg-2 disabled:tw-text-geist-fg-3 disabled:tw-cursor-not-allowed";
const RESET_STYLE = { WebkitAppearance: "none", boxShadow: "none" };

export const GeistInput = React.forwardRef(({
  label, required, error, className = "", wrapperClassName = "", type = "text", rows, ...props
}, ref) => {
  const isTextarea = type === "textarea";
  const Component = isTextarea ? "textarea" : "input";
  return (
    <div className={wrapperClassName}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <Component
        ref={ref}
        type={isTextarea ? undefined : type}
        rows={isTextarea ? rows || 3 : undefined}
        style={RESET_STYLE}
        className={`${BASE} ${isTextarea ? "tw-py-2 tw-resize-none" : "tw-h-10"} ${error ? "tw-border-geist-error" : ""} ${className}`}
        {...props}
      />
      {error && <p className="tw-text-xs tw-text-geist-error tw-mt-1 tw-mb-0">{error}</p>}
    </div>
  );
});
GeistInput.displayName = "GeistInput";
GeistInput.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  error: PropTypes.node,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  type: PropTypes.string,
  rows: PropTypes.number,
};

export const GeistSelect = ({ label, required, options = [], placeholder, error, className = "", wrapperClassName = "", ...props }) => (
  <div className={wrapperClassName}>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <div className="tw-relative">
      <select
        style={RESET_STYLE}
        className={`${BASE} tw-h-10 tw-pr-8 ${error ? "tw-border-geist-error" : ""} ${className}`}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <i className="bi bi-chevron-down tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-[11px] tw-text-geist-fg-3" />
    </div>
    {error && <p className="tw-text-xs tw-text-geist-error tw-mt-1 tw-mb-0">{error}</p>}
  </div>
);
GeistSelect.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
  })),
  placeholder: PropTypes.string,
  error: PropTypes.node,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
};

export default GeistInput;
